import {
  LayoutDashboard,
  CalendarDays,
  Handshake,
  CalendarClock,
  Megaphone,
  MessageSquare,
  Bell,
  MapPinned,
  Presentation,
  BookOpen,
  Settings,
  type LucideIcon,
} from 'lucide-react'

export type AppNavigationKey =
  | 'dashboard'
  | 'events'
  | 'offers'
  | 'calendar'
  | 'communication'
  | 'messages'
  | 'notifications'
  | 'destinations'
  | 'presentations'
  | 'catalogue'
  | 'settings'
export interface AppNavigationItem {
  key: AppNavigationKey
  href: string
  labelKey: string
  shortLabelKey?: string
  icon: LucideIcon
  section: 'primary' | 'inbox' | 'tools' | 'settings'
  badgeSource?: 'messages' | 'notifications'
  operatorOnly?: boolean
  destinationOnly?: boolean
  presentationsOnly?: boolean
  /** Part of the offer module; hidden when the active organization has switched it off. */
  offersModule?: boolean
  tag?: string
}

const ITEMS: readonly AppNavigationItem[] = [
  {
    key: 'dashboard',
    href: '/dashboard',
    labelKey: 'nav.dashboard',
    icon: LayoutDashboard,
    section: 'primary',
  },
  {
    key: 'events',
    href: '/events',
    labelKey: 'nav.events',
    icon: CalendarDays,
    section: 'primary',
  },
  {
    key: 'offers',
    href: '/offers',
    labelKey: 'nav.offers',
    icon: Handshake,
    section: 'primary',
    operatorOnly: true,
    offersModule: true,
  },
  {
    key: 'calendar',
    href: '/calendar',
    labelKey: 'nav.calendar',
    icon: CalendarClock,
    section: 'primary',
  },
  {
    key: 'communication',
    href: '/kommunikasjon',
    labelKey: 'nav.kommunikasjon',
    icon: Megaphone,
    // F6 (22.10.2026): outbound work sits with the organiser's work, not under «Innboks»,
    // which now holds only what comes IN: conversations and notifications.
    section: 'primary',
    operatorOnly: true,
  },
  {
    key: 'messages',
    href: '/messages',
    labelKey: 'nav.messages',
    icon: MessageSquare,
    section: 'inbox',
    badgeSource: 'messages',
  },
  {
    key: 'notifications',
    href: '/notifications',
    labelKey: 'nav.notifications',
    icon: Bell,
    section: 'inbox',
    badgeSource: 'notifications',
  },
  {
    key: 'destinations',
    href: '/destinations',
    labelKey: 'nav.destinations',
    icon: MapPinned,
    section: 'tools',
    destinationOnly: true,
  },
  {
    key: 'presentations',
    href: '/presentations',
    labelKey: 'nav.presentations',
    icon: Presentation,
    section: 'tools',
    presentationsOnly: true,
  },
  {
    key: 'catalogue',
    href: '/catalogue',
    labelKey: 'catalogue.navLabel',
    icon: BookOpen,
    section: 'tools',
    operatorOnly: true,
    offersModule: true,
  },
  {
    key: 'settings',
    href: '/settings',
    labelKey: 'settings.title',
    icon: Settings,
    section: 'settings',
  },
]

/** Every entry, unfiltered: for restoring a remembered menu by key (`useAppNavigation`). */
export function appNavigationItems(): readonly AppNavigationItem[] {
  return ITEMS
}

/** Presentation metadata only. Route guards and database policies remain authoritative. */
export function appNavigation(
  context: { isOperator: boolean; isPro: boolean; presentations: boolean; canAccessDestination?: boolean; offersEnabled?: boolean },
  platform: 'desktop' | 'mobile',
): AppNavigationItem[] {
  return ITEMS.flatMap((item) => {
    if (item.operatorOnly && !context.isOperator) return []
    if (item.offersModule && context.offersEnabled === false) return []
    if (item.destinationOnly && !(context.canAccessDestination ?? context.isOperator)) return []
    if (item.presentationsOnly && !context.presentations) return []
    // Preserve the existing mobile upgrade entry without presenting the paid
    // catalogue itself as accessible to a non-Pro account.
    if (item.key === 'catalogue' && !context.isPro) {
      return platform === 'mobile' ? [{ ...item, href: '/pricing', tag: 'PRO' }] : []
    }
    return [{ ...item }]
  })
}

/**
 * A detail page belongs to the same navigation destination as its list. An `/event/:id` page can
 * be an event or an offer; `trailRoot` (the first crumb the page published) says which, so an
 * offer lights «Tilbud», not «Arrangementer» (offers review 25.10.2026).
 */
export function isAppNavigationActive(pathname: string, href: string, trailRoot?: string): boolean {
  if (pathname.startsWith('/event/') && trailRoot) return href === trailRoot
  if (href === '/events' && (pathname === '/create-event' || pathname.startsWith('/event/')))
    return true
  return pathname === href || pathname.startsWith(href + '/')
}
